// components/Disc/DiscMappers.jsx
//
// Geometry + layout lookup tables for the radial "disc" (a Mosaico-style
// sunburst). Two tables:
//
// 1. yLevelsMapper — radial placement per hierarchy depth.
//      y  = inner radius as a fraction of the disc radius (0 = center, 1 = edge)
//      dy = ring thickness as a fraction
//    So an arc at depth d occupies the radial band [y, y+dy]. Depths map to the
//    curriculum hierarchy: 1 = Area (inner), 2 = Competency (middle),
//    3 = Skill (outer). A deliberate gap between Competency (ends at 0.38+0.22
//    = 0.60) and Skill (starts at 0.64) visually separates the actionable
//    Skill ring from the grouped Area+Competency rings.
//
// 2. level3Mapper — precomputed grid arrangement for the N skills inside a
//    single competency wedge (keyed by N, 1..16). Each entry maps a skill's
//    index to { breakline, columns }:
//      breakline = true  → this skill starts a new row within the wedge
//      columns           → how many skills share that row
//    This lets DrawDisc tile many skills into a tidy grid inside one wedge
//    instead of slicing the wedge into many thin arcs.

export const yLevelsMapper = {
  0: { y: 0.0, dy: 0.0 }, // root (not drawn)

  // Inner two rings - grouped together, no gap
  1: { y: 0.18, dy: 0.2 }, // Area (inner ring)
  2: { y: 0.38, dy: 0.22 }, // Competency (middle ring)

  // VISUAL GAP HERE (0.60 - 0.64 = 0.04 gap)

  // Outer ring - separated from inner rings
  3: { y: 0.64, dy: 0.32 }, // Skill (outer ring) - starts at 0.64 instead of 0.60
};

// Grid layout for level 3 (skills within competency wedges) — see header note.
// Keyed by the number of skills in the wedge (N); each index → row break +
// column count, hand-tuned so N skills tile evenly.
export const level3Mapper = {
  16: {
    0: { breakline: true, columns: 4 },
    1: { breakline: false, columns: 4 },
    2: { breakline: false, columns: 4 },
    3: { breakline: false, columns: 4 },
    4: { breakline: true, columns: 4 },
    5: { breakline: false, columns: 4 },
    6: { breakline: false, columns: 4 },
    7: { breakline: false, columns: 4 },
    8: { breakline: true, columns: 4 },
    9: { breakline: false, columns: 4 },
    10: { breakline: false, columns: 4 },
    11: { breakline: false, columns: 4 },
    12: { breakline: true, columns: 4 },
    13: { breakline: false, columns: 4 },
    14: { breakline: false, columns: 4 },
    15: { breakline: false, columns: 4 },
  },
  12: {
    0: { breakline: true, columns: 3 },
    1: { breakline: false, columns: 3 },
    2: { breakline: false, columns: 3 },
    3: { breakline: true, columns: 3 },
    4: { breakline: false, columns: 3 },
    5: { breakline: false, columns: 3 },
    6: { breakline: true, columns: 3 },
    7: { breakline: false, columns: 3 },
    8: { breakline: false, columns: 3 },
    9: { breakline: true, columns: 3 },
    10: { breakline: false, columns: 3 },
    11: { breakline: false, columns: 3 },
  },
  11: {
    0: { breakline: true, columns: 2 },
    1: { breakline: false, columns: 2 },
    2: { breakline: true, columns: 3 },
    3: { breakline: false, columns: 3 },
    4: { breakline: false, columns: 3 },
    5: { breakline: true, columns: 3 },
    6: { breakline: false, columns: 3 },
    7: { breakline: false, columns: 3 },
    8: { breakline: true, columns: 3 },
    9: { breakline: false, columns: 3 },
    10: { breakline: false, columns: 3 },
  },
  10: {
    0: { breakline: true, columns: 2 },
    1: { breakline: false, columns: 2 },
    2: { breakline: true, columns: 2 },
    3: { breakline: false, columns: 2 },
    4: { breakline: true, columns: 3 },
    5: { breakline: false, columns: 3 },
    6: { breakline: false, columns: 3 },
    7: { breakline: true, columns: 3 },
    8: { breakline: false, columns: 3 },
    9: { breakline: false, columns: 3 },
  },
  9: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 2 },
    2: { breakline: false, columns: 2 },
    3: { breakline: true, columns: 3 },
    4: { breakline: false, columns: 3 },
    5: { breakline: false, columns: 3 },
    6: { breakline: true, columns: 3 },
    7: { breakline: false, columns: 3 },
    8: { breakline: false, columns: 3 },
  },
  8: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
    2: { breakline: true, columns: 3 },
    3: { breakline: false, columns: 3 },
    4: { breakline: false, columns: 3 },
    5: { breakline: true, columns: 3 },
    6: { breakline: false, columns: 3 },
    7: { breakline: false, columns: 3 },
  },
  7: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
    2: { breakline: true, columns: 2 },
    3: { breakline: false, columns: 2 },
    4: { breakline: true, columns: 3 },
    5: { breakline: false, columns: 3 },
    6: { breakline: false, columns: 3 },
  },
  6: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
    2: { breakline: true, columns: 2 },
    3: { breakline: false, columns: 2 },
    4: { breakline: true, columns: 2 },
    5: { breakline: false, columns: 2 },
  },
  5: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
    2: { breakline: true, columns: 1 },
    3: { breakline: true, columns: 2 },
    4: { breakline: false, columns: 2 },
  },
  4: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
    2: { breakline: true, columns: 1 },
    3: { breakline: true, columns: 1 },
  },
  3: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
    2: { breakline: true, columns: 1 },
  },
  2: {
    0: { breakline: true, columns: 1 },
    1: { breakline: true, columns: 1 },
  },
  1: {
    0: { breakline: true, columns: 1 },
  },
};
